/**
 * The programme lead, as the descriptor's Programme Manager contact.
 *
 * @module export/programme-lead
 */

import { SpanStatusCode, trace } from "@opentelemetry/api";

import type { ProgrammePlatformClient, UserSummary } from "@/platform/client";
import type { ProviderContact } from "@/types/provider";

/** The export carries on without the lead, so nothing else reports the failure. */
function recordUnresolvedLead(programmeId: string, error: unknown): void {
  const span = trace.getTracer("export").startSpan("export.programme-lead.unresolved", {
    attributes: { "socratiq.programme.id": programmeId },
  });
  span.recordException({ message: error instanceof Error ? error.message : String(error) });
  span.setStatus({ code: SpanStatusCode.ERROR });
  span.end();
}

/**
 * Name and email of the user holding the programme's `lead` responsibility.
 *
 * A user record holds no title, address or phone, so those stay blank. An
 * unreadable listing or no lead among `users` answers `undefined`, which the
 * export renders as an empty block rather than failing.
 *
 * @param client - Reads the programme's responsibility assignments.
 * @param programmeId - The programme being exported.
 * @param users - The tenant users the caller can see.
 * @returns The lead's contact, or `undefined` when there is none to show.
 */
export async function resolveProgrammeLeadContact(
  client: Pick<ProgrammePlatformClient, "listProgrammeResponsibilities">,
  programmeId: string,
  users: readonly UserSummary[],
): Promise<ProviderContact | undefined> {
  const assignments = await Promise.resolve()
    .then(() => client.listProgrammeResponsibilities(programmeId))
    .catch((error: unknown) => {
      recordUnresolvedLead(programmeId, error);
      return [];
    });
  const user = assignments
    .filter((assignment) => assignment.responsibility === "lead")
    .map((lead) => users.find((candidate) => candidate.id === lead.userId))
    .find((candidate) => candidate !== undefined);
  return user ? { name: user.displayName, email: user.email } : undefined;
}
